import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { useMedia } from '@tager/web-core';

import ArrowIcon from '@/assets/svg/arrow-down.svg';
import PlusIcon from '@/assets/svg/plus.svg';
import MinusIcon from '@/assets/svg/minus.svg';
import MinPrepayment from '@/components/PrepaymentInput/components/MinPrepayment';
import DropdownListPicker from '@/components/Picker/DropdownListPicker';
import { media } from '@/utils/mixin';

import { PrepaymentInputProps } from './PrepaymentInput.types';

const getPrepaymentInteger = (percent: number, total: number) => {
  return Math.round((percent * total) / 100);
};

const getPrepaymentPercents = (min: number, max: number): string[] => {
  let startPercent = min;
  let percents: string[] = [];

  while (startPercent <= max) {
    percents.push(String(startPercent) + '%');
    startPercent++;
  }

  return percents;
};

export default function PrepaymentInput({
  total,
  min,
  max,
  value,
  label = 'Аванс',
  minPrepaymentTitle,
  onChange,
  options,
}: PrepaymentInputProps) {
  const [minPrepayment, maxPrepayment] = [
    getPrepaymentInteger(min, total),
    getPrepaymentInteger(max, total),
  ];

  const [prepayment, setPrepayment] = useState(minPrepayment);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const isTablet = useMedia(`(max-width: 1259px)`);

  const handleChangeInputCounterValue = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = event.target.value;

    if (value.includes('%')) {
      value = value.replace('%', '');
    }

    onChange?.(Number(value));
    setPrepayment(getPrepaymentInteger(Number(value), total));
  };

  const handleChangeInputPrepaymentValue = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const regNumber = /^[0-9]+$/;

    if (regNumber.test(value) || !value) {
      setPrepayment(Number(value));
    }
  };

  const handleCalculatePrepayment = () => {
    if (prepayment <= minPrepayment) {
      onChange?.(min);
      setPrepayment(minPrepayment);
      return;
    } else if (prepayment >= maxPrepayment) {
      onChange?.(max);
      setPrepayment(maxPrepayment);
      return;
    }

    const differenceValuesPercent = Math.floor((prepayment / total) * 100);

    setPrepayment(Number(getPrepaymentInteger(differenceValuesPercent, total)));
    onChange?.(differenceValuesPercent);
  };

  const handleIncrease = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (value < max) {
      onChange?.(Number(value) + 1);
      setPrepayment(getPrepaymentInteger(value + 1, total));
      event.stopPropagation();
    }
  };

  const handleDecrease = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (value > min) {
      onChange?.(Number(value) - 1);
      setPrepayment(getPrepaymentInteger(value - 1, total));
      event.stopPropagation();
    }
  };

  const handlePrepaymentPercentInputClick = () => {
    if (isTablet) {
      setDropdownVisible(!isDropdownVisible);
    } else {
      return;
    }
  };

  useEffect(() => {
    setPrepayment(getPrepaymentInteger(value, total));
  }, [total, value]);

  return (
    <Component>
      <Label>{label}</Label>
      <InputsWrapper>
        <InputContainer>
          <Input
            type="tel"
            value={Number(prepayment).toString()}
            onChange={handleChangeInputPrepaymentValue}
            onBlur={handleCalculatePrepayment}
            isWhiteBackground={options?.isWhiteBackground}
          />
        </InputContainer>

        <InputContainer onClick={handlePrepaymentPercentInputClick}>
          <CounterInput
            readOnly
            type="text"
            value={value + '%'}
            onChange={handleChangeInputCounterValue}
            isWhiteBackground={options?.isWhiteBackground}
          />

          <Buttons>
            <CalcButton inc onClick={handleIncrease} disabled={value === max}>
              {!isTablet ? <ArrowIcon /> : <PlusIcon />}
            </CalcButton>
            <CalcButton dec onClick={handleDecrease} disabled={value === min}>
              {!isTablet ? <ArrowIcon /> : <MinusIcon />}
            </CalcButton>
          </Buttons>

          {isTablet && isDropdownVisible && (
            <StyledDropdownListPicker
              activeValue={String(value) + '%'}
              list={getPrepaymentPercents(min, max)}
              onClick={(value) => {
                onChange?.(Number(value.replace('%', '')));
              }}
              onClose={() => setDropdownVisible(false)}
            />
          )}
        </InputContainer>
      </InputsWrapper>

      {min !== 0 && (
        <MinPrepayment title={minPrepaymentTitle} minPrepayment={min} />
      )}
    </Component>
  );
}

const Component = styled.div`
  display: block;
  width: 100%;
  user-select: none;
`;

const Label = styled.div`
  font-size: 12px;
  line-height: 14px;
  text-transform: uppercase;
  color: ${(props) => props.theme.black200_alpha40};
`;

const InputsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
`;

const InputContainer = styled.div`
  position: relative;

  &:first-child {
    width: 100%;
  }

  ${media.tablet(css`
    width: 100%;
  `)}

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
  }

  input[type='number'] {
    -moz-appearance: textfield !important;
  }
`;

const Input = styled.input<{
  isWhiteBackground?: boolean;
}>`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px;
  background: ${(props) => props.theme.white_1};
  border: 1px solid ${(props) => props.theme.black200_alpha40_1};
  box-sizing: border-box;
  border-radius: 5px 0 0 5px;
  width: 100%;
  height: 42px;
  color: ${(props) => props.theme.black};

  ${({ isWhiteBackground }) =>
    isWhiteBackground &&
    css`
      border: 1px solid ${(props) => props.theme.white_1};
    `}
`;

const CounterInput = styled(Input)<{
  isWhiteBackground?: boolean;
}>`
  border-radius: 0 5px 5px 0;
  max-width: 115px;
  padding: 17px 15px 17px 12px;
  border-left: none;

  ${media.tablet(css`
    max-width: none;
    text-align: center;
  `)}

  ${({ isWhiteBackground }) =>
    isWhiteBackground &&
    css`
      border-left: 1px solid ${(props) => props.theme.black200_alpha40_1};
    `}
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: absolute;
  top: 0;
  right: 5px;

  ${media.tablet(css`
    flex-direction: row-reverse;
    width: 100%;
    justify-content: space-between;
    right: 0;
  `)}
`;

const CalcButton = styled.button<{
  inc?: boolean;
  dec?: boolean;
}>`
  margin: 3px 0;

  ${media.tablet(css`
    margin: 0;
    display: flex;
    align-items: center;
    padding: 0 15%;
  `)}

  &:disabled {
    opacity: 0.3;
  }

  & > svg {
    width: 12px;
    fill: ${(props) => props.theme.black100};

    rect {
      fill: ${(props) => props.theme.black100};
    }

    path {
      fill: ${(props) => props.theme.black100};
    }
  }

  ${({ inc }) =>
    inc &&
    css`
      transform: rotate(180deg);
    `}
`;

const StyledDropdownListPicker = styled(DropdownListPicker)`
  position: absolute;
  margin-top: 5px;
`;
